/**
 * ポータル: 陸上勤怠（船員アプリ・パートや陸上社員のスマホ用）
 * 出勤・退勤のワンタッチ打刻と、自分の月次一覧。
 */
import { useCallback, useEffect, useState } from 'react';
import { AppHeader } from '../../components/AppHeader';
import { BottomNav } from '../../components/BottomNav';
import { apiFetch } from '../../services/api';
import { inputStyle  } from './ui';

interface Row {
  id: string; work_date: string; clock_in: string | null; clock_out: string | null;
  break_minutes: number; note: string | null; work_minutes: number | null;
}

const thisMonth = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 7);
const fmtMin = (m: number | null | undefined) => (m === null || m === undefined) ? '' : `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;

export default function PortalOfficePage() {
  const [today, setToday] = useState<Row | null>(null);
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState<{ rows: Row[]; total_minutes: number; days: number } | null>(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const t = await apiFetch<{ data: Row | null }>('/portal/office/today');
      setToday(t.data);
      const m = await apiFetch<{ data: { rows: Row[]; total_minutes: number; days: number } }>(`/portal/office/me?month=${month}`);
      setData(m.data);
    } catch { /* noop */ }
  }, [month]);
  useEffect(() => { void load(); }, [load]);

  const punch = async (type: 'in' | 'out') => {
    setMsg('');
    try {
      const r = await apiFetch<{ data: Row }>('/portal/office/punch', { method: 'POST', body: JSON.stringify({ type }) });
      setToday(r.data);
      setMsg(type === 'in' ? `出勤を打刻しました（${r.data.clock_in}）` : `退勤を打刻しました（${r.data.clock_out}）`);
      void load();
    } catch (e) { setMsg(e instanceof Error ? e.message : '打刻に失敗しました'); }
  };

  return (
    <div style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      <AppHeader title="陸上勤怠" subtitle="出勤・退勤の打刻（陸上勤務用）" />

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 14px 90px' }}>
        {/* 打刻カード */}
        <div style={{ background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 16, padding: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
            {today?.clock_in ? `本日の出勤: ${today.clock_in}` : '本日の出勤打刻はまだです'}
            {today?.clock_out && `　退勤: ${today.clock_out}`}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button disabled={!!today?.clock_in} onClick={() => void punch('in')}
              style={{
                flex: 1, padding: '16px 0', borderRadius: 14, border: 'none', fontSize: 16, fontWeight: 900,
                cursor: today?.clock_in ? 'not-allowed' : 'pointer',
                background: today?.clock_in ? '#e2e8f0' : 'linear-gradient(135deg,#0f766e,#0f766e)',
                color: today?.clock_in ? '#64748b' : '#fff',
              }}>出勤</button>
            <button disabled={!today?.clock_in || !!today?.clock_out} onClick={() => void punch('out')}
              style={{
                flex: 1, padding: '16px 0', borderRadius: 14, border: 'none', fontSize: 16, fontWeight: 900,
                cursor: (!today?.clock_in || today?.clock_out) ? 'not-allowed' : 'pointer',
                background: (!today?.clock_in || today?.clock_out) ? '#e2e8f0' : 'linear-gradient(135deg,#f59e0b,#d97706)',
                color: (!today?.clock_in || today?.clock_out) ? '#64748b' : '#fff',
              }}>退勤</button>
          </div>
          {msg && <div style={{ fontSize: 13, color: '#0f766e', fontWeight: 700, marginTop: 10, textAlign: 'center' }}>{msg}</div>}
        </div>

        {/* 月次 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <input type="month" style={{ ...inputStyle, width: 160 }} value={month} onChange={e => setMonth(e.target.value)} />
          {data && (
            <span style={{ fontSize: 12.5, color: '#64748b' }}>
              出勤 <strong>{data.days}</strong>日 ・ 実働 <strong>{fmtMin(data.total_minutes)}</strong>
            </span>
          )}
        </div>
        <div style={{ background: '#fff', border: '1.5px solid #e4e9f0', borderRadius: 14, overflow: 'hidden' }}>
          {!data || data.rows.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: 13.5, padding: 24 }}>この月の記録はありません</p>
          ) : data.rows.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid #f1f5f9', fontSize: 13.5 }}>
              <span style={{ width: 76, color: '#334155', fontWeight: 700 }}>{r.work_date.slice(5).replace('-', '/')}</span>
              <span style={{ color: '#0f172a' }}>{r.clock_in ?? '—'} 〜 {r.clock_out ?? '—'}</span>
              <span style={{ marginLeft: 'auto', color: '#0f766e', fontWeight: 700 }}>{fmtMin(r.work_minutes)}</span>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11.5, color: '#64748b', marginTop: 10 }}>時刻の修正が必要なときは陸上管理者に依頼してください。</p>
      </div>

      <BottomNav />
    </div>
  );
}
